import { Component, OnInit } from '@angular/core';
import { ClientService } from '../../../../services/client.service';
import { PropertyService } from '../../../../services/property.service';
import { forkJoin } from 'rxjs';
import { EnvironmentService } from '../../../../services/environment.service';
import { MatDialog } from '@angular/material/dialog';
import { ImageGalleryComponent } from '../../../shared/image-gallery/image-gallery.component';
import { ContractService } from '../../../../services/contract.service';
import { ServiceService } from '../../../../services/service.service';
import { OverlayService } from '../../../../services/overlay.service';
import { LegalService } from '../../../../services/legal.service';
import {
	AccessibilityHelp,
	Alignment,
	AutoLink,
	Autosave,
	BalloonToolbar,
	BlockQuote,
	Bold,
	Essentials,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	Indent,
	IndentBlock,
	Italic,
	Link,
  List,
	Paragraph,
	RemoveFormat,
	SelectAll,
	SpecialCharacters,
	Strikethrough,
	Underline,
	Undo,
  ClassicEditor
} from 'ckeditor5';

import translations from 'ckeditor5/translations/de.js';
@Component({
  selector: 'app-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss']
})
export class ClientComponent implements OnInit {
  client = null;
  allProperties = null;
  clientProperties = null;

  clientConfiguration = [];
  contracts = [];
  services = [];
  legalInformation = [];

  parameterData = null;
  officeHours = null;
  address = null;

  basicDataHasChanged = false;
  serviceHasChanged = [];
  addressHasChanged = false;
  legalHasChanged = [];

  serviceFilter = null;

  public Editor = ClassicEditor;
  public config = {
    toolbar: {
      items: [
        'undo',
        'redo',
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'link',
        'alignment',
        '|',
        'bulletedList',
        'numberedList',
        'outdent',
        'indent'
      ],
      shouldNotGroupWhenFull: true
    },
    plugins: [
      AccessibilityHelp,
      Alignment,
      AutoLink,
      Autosave,
      BalloonToolbar,
      BlockQuote,
      Bold,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      Indent,
      IndentBlock,
      Italic,
      Link,
      Paragraph,
      RemoveFormat,
      SelectAll,
      SpecialCharacters,
      Strikethrough,
      Underline,
      Undo,
      List
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph'
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Überschrift 1',
          class: 'ck-heading_heading1'
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Überschrift 2',
          class: 'ck-heading_heading2'
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Überschrift 3',
          class: 'ck-heading_heading3'
        },   
        {
          model: 'heading4',
          view: 'h4',
          title: 'Überschrift 4',
          class: 'ck-heading_heading4'
        },    
   
      ]
    },
    htmlSupport: {
      allow: [
        {
          name: /^.*$/,
          styles: true,
          attributes: true,
          classes: true
        }
      ]
    },
    language: 'de',
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file'
          }
        }
      }
    },
    placeholder: '',
    translations: [translations]
  }

  legalSections = [
    { title: 'Teilnahmebedingungen', key: 'participation' },
    { title: 'Datenschutzhinweise', key: 'privacy' },
    { title: 'Impressum', key: 'about' },
    { title: 'Kontakt', key: 'contact' }
  ];

  imageProperties = [
    {showValue: 'Logo', key: 'logo', value: null, hint: 'Format: 180 x 90, Typ: .png', hasChanged: false },
    {showValue: 'Favicon', key: 'favicon.path', value: null, hint: 'Format: 16 x 16, Typ: .ico', hasChanged: false },
    {showValue: 'Appleicon', key: 'appletouchicon.path', value: null, hint: 'Format: 76 x 76, Typ: .png', hasChanged: false }
  ];

  constructor(private clientService: ClientService, private propertyService: PropertyService, private environmentService: EnvironmentService,
    private contractService: ContractService, private serviceService: ServiceService, private overlayService: OverlayService,
    private legalService: LegalService, private dialog: MatDialog) { }

  ngOnInit() {
    this.propertyService.getProperty();

    let obs = [
      this.propertyService.getProperties(),
      this.propertyService.getPropertyConfiguration(),
      this.clientService.getClientAddress(),

    ];

    forkJoin<any[]>(obs).subscribe(res => {
      this.allProperties = res[0]['content'];
      this.clientProperties = res[1]['content'];
      this.address = res[2];

      if (!this.address.location) {
        this.address.location = {};
      }

      this.extractProperties();
      this.loadBasisInformation();
      this.setClientConfiguration();
    });
  }

  setClientConfiguration() {
    const toSkip = this.imageProperties.map(prop => prop.key);
    toSkip.push('officehours.notification');

    this.clientConfiguration = Object.keys(this.allProperties).map(key => {
      for (let p of this.clientProperties) {
        if (p.publicId == key && toSkip.indexOf(key) < 0) {
          return { ... this.allProperties[key], value: p.value, publicId: key }
        }
      }
      return false
    }).filter(val => val);
  }

  extractProperties() {
    for (let p in this.clientProperties) {
      const property = this.clientProperties[p];
      if (property.publicId == 'officehours.notification') {
        this.officeHours = JSON.parse(property['value']).map(entry => {
          return { value: entry, hasChanged: false }
        });
      } else {
        const imageKeys = this.imageProperties.map(val => val.key);
        const index = imageKeys.indexOf(property['key']);
        if (index >= 0) {
          this.updateImageSrc(property, index);
        }
      }
    }
  }

  tabChanged(event) {
    this.loadContentFor(event.index);
  }

  loadContentFor(tabIndex) {
    switch (tabIndex) {
      case 0: {
        this.loadBasisInformation();
        break;
      }
      case 2: {
        this.loadLegalInformation();
        break;
      }
      case 3: {
        this.loadServiceInformation();
        break;
      }
    }
  }

  loadBasisInformation() {
    if (!this.client) {
      this.clientService.getClient().subscribe(res => {
        this.client = res;
      });
    }
  }

  loadServiceInformation() {
    if (this.contracts.length == 0 || this.services.length == 0) {
      forkJoin(this.serviceService.getServices(), this.contractService.getContracts()).subscribe((res:any) => {
        this.contracts = res[1]['content'];
        this.services = res[0]['content'].map(service => {
          service.value = this.getContractValue(service.path);
          service.active = this.hasContract(service.path);
          return service;
        });
      });
    }
  }

  updateContractStatus(service) {
    if (service.active) {
      this.contractService.enableOrUpdateContract(service.publicId, service.value).subscribe(() => {
        this.overlayService.openSnackBar('Service erfolgreich aktiviert');
      },
        err => {
          this.overlayService.openSnackBar(`Fehler beim Aktivieren des Services: ${err}`, 'error');
        });
    } else {
      this.contractService.disableContract(this.getContract(service.publicId).publicId).subscribe(() => {
        this.overlayService.openSnackBar('Service erfolgreich deaktiviert');
      },
        err => {
          this.overlayService.openSnackBar(`Fehler beim Deaktivieren des Services: ${err}`, 'error');
        });
    }
  }

  updateContract(service) {
    this.contractService.enableOrUpdateContract(service.publicId, service.value).subscribe(() => {
      this.overlayService.openSnackBar('Service erfolgreich aktualisiert');
    },
      err => {
        this.overlayService.openSnackBar(`Fehler beim Aktualisieren des Services: ${err}`, 'error');
      });
  }

  getContractValue(servicePath) {
    for (let contract of this.contracts) {
      if (contract.path === servicePath) {
        return contract.value;
      }
    }
  }

  hasContract(servicePath) {
    let result = false;
    for (let contract of this.contracts) {
      if (servicePath == contract.path) {
        result = true;
        break;
      }
    }
    return result;
  }

  getContract(publicId) {
    let result = undefined;

    for (let contract of this.contracts) {
      if (publicId == contract.servicePublicId) {
        result = contract;
        break;
      }
    }

    return result;
  }

  serviceFilterChanged(filter) {
    this.serviceFilter = filter;
  }

  loadLegalInformation() {
    if (this.legalInformation.length == 0) {
      this.legalService.getLegalFiles().subscribe(res => {
        this.legalInformation = res;
      });
    }
  }

  legalEditorChanged(e, index) {
    if (e.event == 'text-change' && e.source == 'user') {
      this.legalHasChanged[index] = true;
    }
  }

  saveLegal(index, name) {
    const toSave = this.legalInformation[index];
    this.legalService.updateLegalFile(name, toSave).subscribe((res) => {
      this.overlayService.openSnackBar('Text erfolgreich aktualisiert.');
    }, err => {
      this.overlayService.openSnackBar(`Fehler beim Aktualisieren: ${err.error.message}`, 'error');
    });
  }

  saveProperty(property) {
    this.propertyService.updateProperty(property.publicId, property.value).subscribe(() => {
      this.overlayService.openSnackBar('Wert erfolgreich aktualisiert.');
    }, err => {
      this.overlayService.openSnackBar(`Fehler beim Aktualisieren: ${err.error.message}`, 'error');
    });
  }

  saveImage(imageProperty) {
    const toSave = {
      value: imageProperty.newValue,
      publicId: imageProperty.key
    }
    this.saveProperty(toSave);
  }

  removeHour(index) {
    this.officeHours.splice(index, 1);
    this.saveOfficeHours();
  }

  addOfficeHourRow() {
    this.officeHours.push({ value: '', hasChanged: false })
  }

  saveOfficeHours() {
    const hoursString = JSON.stringify(this.officeHours.map(entry => entry.value));
    this.propertyService.updateProperty('officehours.notification', hoursString).subscribe(() => {
      this.overlayService.openSnackBar('Wert erfolgreich aktualisiert.');
    }, err => {
      this.overlayService.openSnackBar(`Fehler beim Aktualisieren: ${err.error.message}`, 'error');
    });
  }

  saveBasicData() {
    this.clientService.updateClient(this.client).subscribe(() => {
      this.overlayService.openSnackBar('Wert erfolgreich aktualisiert.');
    }, err => {
      this.overlayService.openSnackBar(`Fehler beim Aktualisieren: ${err.error.message}`, 'error');
    });
  }

  getCoordinates() {
    // this.clientService.getGoogleAddressCoordinates(this.address).subscribe(res => {
    //   console.log('google: ', res);
    // });
  }

  saveAddress() {
    this.clientService.updateClientAddress(this.address).subscribe(() => {
      this.overlayService.openSnackBar('Wert erfolgreich aktualisiert.');
    }, err => {
      this.overlayService.openSnackBar(`Fehler beim Aktualisieren: ${err.error.message}`, 'error');
    });
  }

  updateImageSrc(property, propertyImageIndex) {
    let result: String;

    if (property.value != null) {
      switch (property.key) {
        case 'appletouchicon.path':
        case 'favicon.path':
          result = this.clientService.getFileURL(property.value);
          break;
        case 'logo':
          result = this.clientService.getAttachmentURL(property.value);
          break;
        default: ''
      }
    }
    this.imageProperties[propertyImageIndex].value = result;
  }

  openImageGallery(property) {
    const dialogRef = this.dialog.open(ImageGalleryComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        property.value = result.url;
        property.hasChanged = true;
        property.newValue = `${result.clientRef}/attachment/${result.publicId}`;
      }
    });
  }
}
